import { useState, useEffect } from 'react';
import { ArrowLeft, User, Mail, Calendar, Edit2, LogOut, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { userService } from '../services/user.service';
import { UserSettings } from './UserSettings';
import { LoadingSkeleton } from './LoadingSkeleton';
import toast from 'react-hot-toast';
import { handleApiError } from '../lib/api-client';

interface ProfileProps {
  onBack: () => void;
}

export function Profile({ onBack }: ProfileProps) {
  const { user, signOut } = useAuth();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Fetch user profile - use same query key as UserSettings for cache sharing
  const {
    data: profileData,
    isLoading: profileLoading,
    error: profileError,
    refetch,
  } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: () => userService.getProfile(),
    enabled: !!user,
  });

  // Backend returns user object with nested profile: { id, email, createdAt, profile: {...} | null, healthData: [...] }
  // The service might wrap it in { data: ... } or return directly
  const userData = profileData?.data || profileData || {};
  const profileInfo = userData.profile || {};
  
  // Debug: Log the structure to help diagnose issues
  useEffect(() => {
    if (profileData) {
      console.log('Profile data structure:', {
        raw: profileData,
        userData,
        profileInfo,
        hasProfile: !!userData.profile,
      });
    }
  }, [profileData, userData, profileInfo]);
  
  // Merge user data with profile data for easier access
  const mergedProfile = {
    ...userData,
    ...profileInfo,
    email: userData.email || profileInfo.email || user?.email || '',
    createdAt: userData.createdAt || profileInfo.createdAt,
  };

  // Refetch when settings modal closes (in case profile was updated)
  useEffect(() => {
    if (!showSettings) {
      refetch();
      // Also invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    }
  }, [showSettings, refetch, queryClient]);

  // Toast on error - must run before any early return (hooks rule)
  useEffect(() => {
    if (profileError) {
      console.error('Profile fetch error:', profileError);
      toast.error(`Failed to load profile: ${handleApiError(profileError)}`);
    }
  }, [profileError]);

  if (profileLoading) {
    return <LoadingSkeleton />;
  }

  // Extract display values with proper fallbacks
  const displayName = mergedProfile.displayName || mergedProfile.username || user?.email?.split('@')[0] || 'User';
  const email = mergedProfile.email || user?.email || '';
  
  // Handle createdAt - could be from user or profile
  let createdAt = 'N/A';
  if (mergedProfile.createdAt) {
    try {
      createdAt = new Date(mergedProfile.createdAt).toLocaleDateString();
    } catch (e) {
      console.error('Error parsing createdAt:', e);
    }
  } else if (userData.createdAt) {
    try {
      createdAt = new Date(userData.createdAt).toLocaleDateString();
    } catch (e) {
      console.error('Error parsing user createdAt:', e);
    }
  }
  
  const bio = mergedProfile.bio || '';
  
  let dateOfBirth = null;
  if (mergedProfile.dateOfBirth) {
    try {
      dateOfBirth = new Date(mergedProfile.dateOfBirth).toLocaleDateString();
    } catch (e) {
      console.error('Error parsing dateOfBirth:', e);
    }
  }
  
  const gender = mergedProfile.gender || '';
  const height = mergedProfile.height || null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 pb-20">
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-700 hover:text-dropsugar-green transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Profile</h1>
            <button
              onClick={() => {
                refetch();
                queryClient.invalidateQueries({ queryKey: ['user-profile'] });
                queryClient.invalidateQueries({ queryKey: ['profile'] });
                toast.success('Profile refreshed');
              }}
              className="p-2 text-gray-700 hover:text-dropsugar-green hover:bg-dropsugar-green/10 rounded-lg transition-colors"
              title="Refresh profile"
              aria-label="Refresh profile"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="flex items-center space-x-6 mb-6">
            <div className="w-24 h-24 bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark rounded-full flex items-center justify-center">
              <User className="w-12 h-12 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{displayName}</h2>
              <div className="flex items-center space-x-2 text-gray-600">
                <Mail className="w-4 h-4" />
                <span>{email}</span>
              </div>
            </div>
            <button
              onClick={() => setShowSettings(true)}
              className="px-4 py-2 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white rounded-lg font-semibold transition-colors flex items-center space-x-2"
            >
              <Edit2 className="w-4 h-4" />
              <span>Edit</span>
            </button>
          </div>

          {bio && (
            <div className="border-t border-gray-200 pt-4 mt-4">
              <p className="text-gray-700">{bio}</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Account Information</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700">Email</span>
              </div>
              <span className="text-gray-900 font-medium">{email}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700">Member Since</span>
              </div>
              <span className="text-gray-900 font-medium">{createdAt}</span>
            </div>
            {mergedProfile.role && (
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center space-x-3">
                  <User className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-700">Role</span>
                </div>
                <span className="px-3 py-1 bg-dropsugar-green/20 text-dropsugar-green-dark rounded-full text-sm font-semibold">
                  {mergedProfile.role}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Actions</h3>
          <div className="space-y-3">
            <button
              onClick={() => {
                if (confirm('Are you sure you want to sign out?')) {
                  signOut();
                }
              }}
              className="w-full px-4 py-3 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
            >
              <LogOut className="w-5 h-5" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      {showSettings && (
        <UserSettings
          onClose={() => {
            setShowSettings(false);
            // Refetch profile data after settings close
            setTimeout(() => {
              refetch();
              queryClient.invalidateQueries({ queryKey: ['user-profile'] });
              queryClient.invalidateQueries({ queryKey: ['profile'] });
            }, 500);
          }}
        />
      )}
    </div>
  );
}
